import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MoneyDayService } from '../money-flow/money-day.service';

export interface CustomerAccountOrganization {
  id: number;
  name: string;
  enabled: boolean;
  reconciled: boolean;
}
export interface CustomerAccountChannel {
  bank_id: number;
  org_bank_account_id?: number;
  channel: 'CASH' | 'BANK';
  label: string;
  warehouse_id?: number;
  manager_user_id?: number;
}
export interface CustomerAccountSummary {
  client_id: number;
  org_id: number;
  currency: string;
  version: number;
  enabled: boolean;
  reconciled: boolean;
  blocked_reason?: string | null;
  free_credit: number;
  reserved_credit: number;
  available_credit: number;
  committed_credit: number;
  debt: number;
  balance: number;
  pending_bank: number;
  legacy_credit: number;
  unassigned_documents?: number;
}
export type CustomerAccountKind =
  | 'TOPUP'
  | 'ALLOCATE'
  | 'PAYOUT'
  | 'CONFIRM_BANK'
  | 'CANCEL_PENDING'
  | 'OPENING'
  | 'REVERSE';
export interface CustomerAccountCommand {
  client_id: number;
  org_id: number;
  kind: CustomerAccountKind;
  amount?: number;
  channel?: 'CASH' | 'BANK';
  invoice_id?: number;
  order_id?: number;
  allocations?: Array<{ invoice_id: number; amount: number }>;
  cash_topup?: { amount: number; cashbox_id?: number };
  cashbox_id?: number;
  bank_id?: number;
  org_bank_account_id?: number;
  source_operation_id?: number;
  bank_reference?: string;
  bank_date?: string;
  reason?: string;
}
export interface CustomerAccountPreview {
  token: string;
  version: number;
  expires_at: string;
  allowed: boolean;
  blocked_reasons: Array<{ code: string; message: string }>;
  before: CustomerAccountSummary;
  after: CustomerAccountSummary;
  plan: any;
}
export interface CustomerAccountOperation {
  id: number;
  kind: string;
  status: string;
  amount: number;
  channel?: string;
  created_at?: string;
  occurred_at?: string;
  date?: string;
  reason?: string;
  balance_delta?: number;
  delta_balance?: number;
  balance_after?: number;
  order_id?: number;
  invoice_id?: number;
  actor_name?: string;
  user_name?: string;
  source_operation_id?: number;
  bank_reference?: string;
  error?: string;
  fiscal_status?: string;
  request_json?: any;
  bank_id?: number;
  cashbox_id?: number;
  org_bank_account_id?: number;
}
export interface CustomerAccountInvoice {
  invoice_id: number;
  order_id?: number;
  total?: number;
  paid?: number;
  due: number;
  org_id?: number;
  client_id?: number;
  description?: string;
}
export interface CustomerAccountHistory {
  data: CustomerAccountOperation[];
  length: number;
  opening_balance: number | null;
  closing_balance: number | null;
  as_of?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
}
export interface CustomerAccountReportRow extends CustomerAccountSummary {
  client_name: string;
  client_phone?: string;
  manager_name?: string;
  org_name?: string;
  overdue_debt?: number;
}
export interface CustomerAccountHistoryFilter {
  client_id: number;
  org_id: number;
  date_from?: string;
  date_to?: string;
  kind?: string;
  status?: string;
  limit?: number;
  offset?: number;
  invoice_id?: number;
  actor_user_id?: number;
  order_id?: number;
  as_of?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
}

@Injectable({ providedIn: 'root' })
export class CustomerAccountService {
  private readonly base = `${environment.BACKEND}/client-account`;
  readonly changed = new Subject<{ clientId: number; orgId: number }>();
  constructor(private http: HttpClient, private moneyDay: MoneyDayService) {}
  private params(values: object) {
    let params = new HttpParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '')
        params = params.set(key, String(value));
    });
    return params;
  }
  organizations() {
    return this.http.get<{ data: CustomerAccountOrganization[] }>(`${this.base}/organizations`);
  }
  overview(clientId: number) {
    return this.http.get<any>(`${this.base}/overview`, {
      params: this.params({ client_id: clientId })
    });
  }
  channels(orgId: number) {
    return this.http.get<{ data: CustomerAccountChannel[] }>(`${this.base}/channels`, {
      params: this.params({ org_id: orgId })
    });
  }
  summary(clientId: number, orgId: number) {
    return this.http.get<CustomerAccountSummary>(`${this.base}/summary`, {
      params: this.params({ client_id: clientId, org_id: orgId })
    });
  }
  history(filter: CustomerAccountHistoryFilter) {
    return this.http.get<CustomerAccountHistory>(`${this.base}/history`, {
      params: this.params(filter)
    });
  }
  operation(id: number) {
    return this.http.get<any>(`${this.base}/operation`, {
      params: this.params({ operation_id: id })
    });
  }
  retryReceipt(id: number) {
    return this.http.post(`${this.base}/fiscal/retry`, { operation_id: id });
  }
  invoices(clientId: number, orgId: number) {
    return this.http.get<{ data: CustomerAccountInvoice[] }>(`${this.base}/open-invoices`, {
      params: this.params({ client_id: clientId, org_id: orgId })
    });
  }
  previewReturn(clientId: number, orgId: number, invoiceId: number, amount: number) {
    return this.http.get<{
      goods_amount: number;
      debt_reduction: number;
      released_credit: number;
      available_after: number;
    }>(`${this.base}/preview-return`, {
      params: this.params({ client_id: clientId, org_id: orgId, invoice_id: invoiceId, amount })
    });
  }
  report(filter: {
    sort?: string;
    direction?: 'asc' | 'desc';
    org_id?: number;
    search?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.http.get<{ data: CustomerAccountReportRow[]; length: number }>(
      `${this.base}/report`,
      { params: this.params(filter) }
    );
  }
  preview(command: CustomerAccountCommand) {
    return this.http.post<CustomerAccountPreview>(`${this.base}/preview`, command);
  }
  execute(request: { request_id: string; token: string; version: number }) {
    return this.http
      .post<{
        operation: CustomerAccountOperation;
        summary: CustomerAccountSummary;
        replayed: boolean;
      }>(`${this.base}/execute`, request)
      .pipe(
        tap((result) => {
          this.changed.next({ clientId: result.summary.client_id, orgId: result.summary.org_id });
          this.moneyDay.notifyMoneyFlowChanged();
        })
      );
  }
  result(requestId: string) {
    return this.http
      .get<{
        operation: CustomerAccountOperation;
        summary: CustomerAccountSummary;
        replayed: boolean;
      }>(`${this.base}/result`, { params: this.params({ request_id: requestId }) })
      .pipe(
        tap((result) =>
          this.changed.next({ clientId: result.summary.client_id, orgId: result.summary.org_id })
        )
      );
  }
  exportHistory(filter: CustomerAccountHistoryFilter, format: 'pdf' | 'csv') {
    return this.http.get(`${this.base}/history.${format}`, {
      params: this.params(filter),
      responseType: 'blob'
    });
  }
}
